import React, { useState, useEffect, useRef } from 'react';
import './SearchBar.css';
import { useNavigate } from 'react-router-dom';

const SearchBar = ({
  onSearch,
  showFilters = false,
  showCategories = false,
  categories = [],
  selectedCategory = 'All',
  onCategoryChange,
  className = '',
  initialQuery = '',
}) => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState(initialQuery);
  const [recentSearches, setRecentSearches] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);
  const [filters, setFilters] = useState({
    sortBy: 'relevance',
    timeRange: 'all',
    aspectRatio: 'all',
    category: 'All',
  });

  const searchContainerRef = useRef(null);

  // Load recent searches from localStorage on mount
  useEffect(() => {
    const savedSearches = localStorage.getItem('recentSearches');
    if (savedSearches) {
      setRecentSearches(JSON.parse(savedSearches));
    }
  }, []);

  // Close suggestions if clicked outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Update search query when initialQuery changes
  useEffect(() => {
    setSearchQuery(initialQuery);
  }, [initialQuery]);

  const handleSearch = (e, directSearch = false) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    if (!directSearch && (!e || (e.type === 'keydown' && e.key !== 'Enter'))) {
      return;
    }

    const trimmedQuery = searchQuery.trim();
    
    // Always call onSearch, even with empty query
    onSearch({
      query: trimmedQuery,
      ...filters
    });

    // Update recent searches only if there's a query
    if (trimmedQuery) {
      const updatedSearches = [trimmedQuery, ...recentSearches.filter(s => s !== trimmedQuery)].slice(0, 5);
      setRecentSearches(updatedSearches);
      localStorage.setItem('recentSearches', JSON.stringify(updatedSearches));
    }

    setShowSuggestions(false);
  };

  const handleClear = () => {
    setSearchQuery('');
    onSearch({ query: '', category: selectedCategory, ...filters });
  };

  const toggleFilters = () => setIsFiltersOpen((prev) => !prev);

  const handleRecentSearchClick = (search) => {
    setSearchQuery(search);
    setShowSuggestions(false);
    
    // Update URL with search params and current filters
    const params = new URLSearchParams();
    params.set('q', search);
    params.set('page', '1');
    if (selectedCategory !== 'All') {
      params.set('category', selectedCategory);
    }
    // Add filter parameters
    params.set('sortBy', filters.sortBy);
    params.set('timeRange', filters.timeRange);
    params.set('aspectRatio', filters.aspectRatio);
    navigate(`/explore?${params.toString()}`);
  };

  return (
    <div className={`sb-wrapper ${className}`} ref={searchContainerRef}>
      {/* Search Form */}
      <form onSubmit={handleSearch} className="sb-form">
        <div className="sb-container">
          {/* Search Button */}
          <button type="submit" className="sb-btn">
            <svg viewBox="0 0 24 24" className="sb-icon">
              <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 
                16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 
                3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 
                0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 
                14 9.5 14z" />
            </svg>
          </button>

          {/* Input Field */}
          <input
            type="text"
            placeholder="Search for inspiration..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onFocus={() => setShowSuggestions(true)}
            className="sb-input"
          />

          {/* Clear Button */}
          {searchQuery && (
            <button type="button" className="sb-clear" onClick={handleClear}>
              <svg viewBox="0 0 24 24" className="sb-icon">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 
                  5 6.41 10.59 12 5 17.59 6.41 19 12 
                  13.41 17.59 19 19 17.59 13.41 12z" />
              </svg>
            </button>
          )}

          {/* Filters Toggle */}
          {showFilters && (
            <button
              type="button"
              className={`sb-filters-toggle ${isFiltersOpen ? 'active' : ''}`}
              onClick={toggleFilters}
            >
              <svg viewBox="0 0 24 24" className="sb-icon">
                <path d="M3 17v2h6v-2H3zM3 5v2h10V5H3zm10 
                  16v-2h8v-2h-8v-2h-2v6h2zM7 9v2H3v2h4v2h2V9H7zm14 
                  4v-2H11v2h10zm-6-4h2V7h4V5h-4V3h-2v6z" />
              </svg>
            </button>
          )}
        </div>
      </form>

      {/* Recent Searches */}
      {showSuggestions && recentSearches.length > 0 && (
        <div className="sb-suggestions">
          <div className="sb-suggestions-title">Recent Searches</div>
          {recentSearches.map((search, idx) => (
            <div
              key={idx}
              className="sb-suggestion-item"
              onClick={() => handleRecentSearchClick(search)}
            >
              <svg viewBox="0 0 24 24" className="sb-icon sm">
                <path d="M13 3c-4.97 0-9 4.03-9 9H1l3.89 3.89.07.14L9 
                  12H6c0-3.87 3.13-7 7-7s7 3.13 7 7-3.13 7-7 7c-1.93 
                  0-3.68-.79-4.94-2.06l-1.42 1.42C8.27 19.99 10.51 
                  21 13 21c4.97 0 9-4.03 9-9s-4.03-9-9-9zm-1 5v5l4.28 
                  2.54.72-1.21-3.5-2.08V8H12z"/>
              </svg>
              {search}
            </div>
          ))}
        </div>
      )}

      {/* Advanced Filters */}
      {showFilters && isFiltersOpen && (
        <div className="sb-filters">
          <div className="sb-filter-group">
            <label>Sort By</label>
            <select
              value={filters.sortBy}
              onChange={(e) => {
                const newFilters = { ...filters, sortBy: e.target.value };
                setFilters(newFilters);
                const params = new URLSearchParams();
                if (searchQuery.trim()) params.set('q', searchQuery.trim());
                params.set('page', '1');
                if (selectedCategory !== 'All') params.set('category', selectedCategory);
                params.set('sortBy', newFilters.sortBy);
                params.set('timeRange', newFilters.timeRange);
                params.set('aspectRatio', newFilters.aspectRatio);
                navigate(`/explore?${params.toString()}`);
              }}
            >
              <option value="relevance">Relevance</option>
              <option value="latest">Latest</option>
              <option value="popular">Popular</option>
            </select>
          </div>
          <div className="sb-filter-group">
            <label>Time Range</label>
            <select
              value={filters.timeRange}
              onChange={(e) => {
                const newFilters = { ...filters, timeRange: e.target.value };
                setFilters(newFilters);
                const params = new URLSearchParams();
                if (searchQuery.trim()) params.set('q', searchQuery.trim());
                params.set('page', '1');
                if (selectedCategory !== 'All') params.set('category', selectedCategory);
                params.set('sortBy', newFilters.sortBy);
                params.set('timeRange', newFilters.timeRange);
                params.set('aspectRatio', newFilters.aspectRatio);
                navigate(`/explore?${params.toString()}`);
              }}
            >
              <option value="all">All Time</option>
              <option value="day">Last 24 Hours</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
              <option value="year">This Year</option>
            </select>
          </div>
          <div className="sb-filter-group">
            <label>Aspect Ratio</label>
            <select
              value={filters.aspectRatio}
              onChange={(e) => {
                const newFilters = { ...filters, aspectRatio: e.target.value };
                setFilters(newFilters);
                const params = new URLSearchParams();
                if (searchQuery.trim()) params.set('q', searchQuery.trim());
                params.set('page', '1');
                if (selectedCategory !== 'All') params.set('category', selectedCategory);
                params.set('sortBy', newFilters.sortBy);
                params.set('timeRange', newFilters.timeRange);
                params.set('aspectRatio', newFilters.aspectRatio);
                navigate(`/explore?${params.toString()}`);
              }}
            >
              <option value="all">All</option>
              <option value="landscape">Landscape</option>
              <option value="portrait">Portrait</option>
              <option value="square">Square</option>
            </select>
          </div>
        </div>
      )}

      {/* Categories */}
      {showCategories && categories.length > 0 && (
        <div className="sb-categories">
          {categories.map((cat) => (
            <button
              key={cat.label}
              onClick={() => onCategoryChange?.(cat.label)}
              className={`sb-category ${selectedCategory === cat.label ? 'selected' : ''}`}
            >
              {cat.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default SearchBar;
